import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Video, Users, Clock, Bell } from "lucide-react";

const UPCOMING = [
  { name: "Live Blackjack", desc: "Multi-camera HD table · European rules", icon: "🃏", color: "from-emerald-600/40 to-emerald-800/40" },
  { name: "Live Roulette", desc: "Immersive Roulette · Speed / European / Auto", icon: "🎡", color: "from-red-600/40 to-red-800/40" },
  { name: "Live Baccarat", desc: "Speed & Squeeze · Dragon Bonus side bets", icon: "🎴", color: "from-blue-600/40 to-blue-800/40" },
  { name: "Live Game Shows", desc: "Crazy Time · Mega Wheel · Monopoly Live", icon: "🎪", color: "from-purple-600/40 to-purple-800/40" },
  { name: "Live Poker", desc: "Casino Hold'em · Three Card Poker · Ultimate Texas", icon: "♠", color: "from-slate-600/40 to-slate-800/40" },
  { name: "Live Sic Bo", desc: "Super Sic Bo · Lightning Dice · Craps Live", icon: "🎲", color: "from-yellow-600/40 to-yellow-800/40" },
];

export default function LiveCasino() {
  const { lang } = useApp();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const subscribe = async () => {
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await api.post("/live-casino/notify", { email });
      setSubscribed(true);
      toast.success(lang === "hu" ? "Értesíteni fogunk!" : "You'll be notified!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1300px] mx-auto px-6 py-12">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-red-900/30 border border-red-500/40 rounded-full px-4 py-1 text-[10px] uppercase tracking-[0.4em] text-red-300 mb-4">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"/>Coming Soon
        </div>
        <h1 className="font-serif text-5xl md:text-6xl mb-3">Live Casino</h1>
        <p className="text-white/60 max-w-2xl mx-auto">
          {lang === "hu"
            ? "Valódi dealerekkel, 24/7 élő stream, HD kamerák, professzionális stúdiók. Az Evolution Gaming és Pragmatic Live integráció folyamatban a licencünk megszerzése után."
            : "Real dealers, 24/7 live streams, HD cameras, professional studios. Evolution Gaming and Pragmatic Live integrations coming online post-license."}
        </p>
      </div>

      {/* Feature strip */}
      <div className="grid md:grid-cols-4 gap-4 mb-10">
        <div className="lux-card p-4 text-center">
          <Video className="mx-auto text-[#d4af37]" size={24}/>
          <div className="mt-2 text-sm font-bold">4K HD Streams</div>
          <div className="text-[10px] text-white/50 mt-1">Multi-camera pro studios</div>
        </div>
        <div className="lux-card p-4 text-center">
          <Users className="mx-auto text-[#d4af37]" size={24}/>
          <div className="mt-2 text-sm font-bold">Real Dealers</div>
          <div className="text-[10px] text-white/50 mt-1">Trained croupiers · Multi-lingual</div>
        </div>
        <div className="lux-card p-4 text-center">
          <Clock className="mx-auto text-[#d4af37]" size={24}/>
          <div className="mt-2 text-sm font-bold">24/7 Open</div>
          <div className="text-[10px] text-white/50 mt-1">Non-stop live action</div>
        </div>
        <div className="lux-card p-4 text-center">
          <Bell className="mx-auto text-[#d4af37]" size={24}/>
          <div className="mt-2 text-sm font-bold">Chat with Dealer</div>
          <div className="text-[10px] text-white/50 mt-1">Interactive experience</div>
        </div>
      </div>

      {/* Preview cards */}
      <h2 className="font-serif text-2xl mb-4 gold-text">Upcoming Tables</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {UPCOMING.map(g => (
          <div key={g.name} data-testid={`live-preview-${g.name}`}
               className="lux-card overflow-hidden relative">
            <div className={`aspect-video bg-gradient-to-br ${g.color} flex items-center justify-center relative`}>
              <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at center, rgba(212,175,55,.4), transparent 60%)" }}/>
              <div className="text-7xl relative">{g.icon}</div>
              <div className="absolute top-3 left-3 bg-black/60 backdrop-blur border border-red-500/40 rounded px-2 py-0.5 text-[9px] uppercase tracking-widest text-red-300">
                LIVE · Coming
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-serif text-lg">{g.name}</h3>
              <div className="text-white/50 text-xs mt-1">{g.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Notify form */}
      <div className="lux-card p-8 text-center relative overflow-hidden max-w-2xl mx-auto">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 30% 30%, rgba(212,175,55,.2), transparent 60%)" }}/>
        <div className="relative">
          <h3 className="font-serif text-2xl">{lang === "hu" ? "Értesíts, ha él!" : "Notify me when we go live"}</h3>
          <p className="text-white/50 text-sm mt-2">
            {lang === "hu" ? "Email cím megadásával elsőként értesülhetsz." : "Enter your email — you'll be first in line."}
          </p>
          {!subscribed ? (
            <div className="flex gap-2 mt-6 max-w-md mx-auto">
              <input data-testid="live-notify-email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com"
                     className="flex-1 bg-[#1a1a1a] border border-white/10 rounded px-3 py-3 text-sm focus:border-[#d4af37] focus:outline-none"/>
              <button data-testid="live-notify-submit" onClick={subscribe} disabled={busy}
                      className="btn-gold px-5 py-3 rounded text-sm uppercase tracking-widest">
                {busy ? "..." : "Notify me"}
              </button>
            </div>
          ) : (
            <div className="mt-6 text-emerald-400">
              ✓ {lang === "hu" ? "Feliratkozva! Nemsokára hallasz felőlünk." : "Subscribed! You'll hear from us soon."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
